import { useCallback, useEffect, useMemo } from 'react';
import { useIntl } from 'react-intl';

import { useDispatch, usePublicProfiles } from '_common/hooks';
import { openModal } from 'App/ModalContext/utils';
import { listObjects } from '_common/components/Table/TableSlice';

import { UserAvatar } from '_common/components';
import { ThunksTable, Cell, type TableColumn, type TableRow } from '_common/components/Table';

export type TableProps = {
  identity: Table.Identity;
  users: string[];
  group: doDOC.Group;
  removeUserHandler: (user: ParsedProfile<UserPublicProfile>) => void;
  testId: string;
};
const Table = ({ identity, users, group, removeUserHandler, testId }: TableProps) => {
  const intl = useIntl();
  const dispatch = useDispatch();

  const { profiles } = usePublicProfiles(users);

  //#region Table object listing handlers
  const fetchObjects = useCallback(async () => {
    return new Promise<Request.FetchResponse>((resolve) => {
      resolve({
        data: { nodes: Object.values(profiles) },
      });
    });
  }, [profiles]);

  useEffect(() => {
    dispatch(
      listObjects({
        identity: identity,
        fetch: fetchObjects,
        cause: 'INITIAL',
        request: { offset: 0 },
      }),
    );
  }, [fetchObjects]);
  //#endregion

  const columns = useMemo<TableColumn[]>(
    () => [
      {
        id: 'avatar',
        accessorKey: 'avatar',
        enableSorting: false,
        size: 52,
        header: undefined,
        cell: (info) => {
          const profile = profiles[info.row.original.id];

          return (
            <Cell testId={`${profile.id}-avatar-column`}>
              <UserAvatar userId={profile.id} size="m" />
            </Cell>
          );
        },
      },
      {
        id: 'name',
        accessorKey: 'name',
        enableSorting: false,
        header: intl.formatMessage({ id: 'groups.header.name' }),
        cell: (info) => {
          const profile = profiles[info.row.original.id];
          return (
            <Cell testId={`${profile.id}-name-column`} ellipsis>
              {profile.name}
            </Cell>
          );
        },
      },
      {
        id: 'email',
        accessorKey: 'email',
        enableSorting: false,
        size: 320,
        header: intl.formatMessage({ id: 'storage.modals.user.email' }),
        cell: (info) => {
          const profile = profiles[info.row.original.id];
          return <Cell testId={`${profile.id}-email-column`}>{profile.email}</Cell>;
        },
      },
      {
        id: 'permissions',
        accessorKey: 'permissions',
        enableSorting: false,
        size: 160,
        header: intl.formatMessage({ id: 'groups.header.permissions' }),
        cell: (info) => {
          const profile = profiles[info.row.original.id];

          let permission = intl.formatMessage({ id: 'groups.header.member' }, { membersCount: 1 });

          if (group.permissions.users[profile.id]) {
            if (group.permissions.users[profile.id].includes('owner')) {
              permission = intl.formatMessage({ id: 'groups.admin' });
            }
          }

          if (group.owner === profile.id) {
            permission = intl.formatMessage({ id: 'groups.header.owner' });
          }

          return <Cell testId={`${profile.id}-permission-column`}>{permission}</Cell>;
        },
      },
    ],
    [profiles, group],
  );

  const rowsData = useMemo<TableRow[]>(() => {
    return users
      .filter((userId) => profiles[userId])
      .map<TableRow>((objectId) => {
        const object = profiles[objectId];

        const userIsOwner =
          group.user_permissions.includes('admin') || group.user_permissions.includes('owner');
        const subjectIsOwner = group.owner === objectId;

        return {
          ...object,
          suffix:
            userIsOwner && !subjectIsOwner
              ? {
                  actions: [
                    {
                      label: intl.formatMessage({ id: 'groups.removeUser' }),
                      onClick: () => {
                        openModal({
                          modal: 'ConfirmationModal',
                          data: {
                            onConfirm: () => removeUserHandler(object),
                            title: 'REMOVE_SOMETHING_QUOTED',
                            titleValues: { label: object.name },
                            message:
                              'REMOVING_THIS_USER__FROM_GROUP_WILL_REVOKE_PERMISSIONS_CONFIRM',
                            confirmButtonTextId: 'global.remove',
                            confirmButtonType: 'danger',
                            cancelButtonTextId: 'global.cancel',
                            actionValue: { groupId: group.id, userId: object.id },
                            cancelButtonShow: true,
                          },
                        });
                      },
                    },
                  ],
                }
              : undefined,
        };
      });
  }, [profiles, users, group]);

  return (
    <ThunksTable
      identity={identity}
      options={{ columns, data: rowsData }}
      loadingLabel={intl.formatMessage({ id: 'LOADING_ELEMENTS' })}
      fetchObjects={fetchObjects}
      testId={testId}
      selectable={false}
      renderFooter={undefined}
    />
  );
};

export default Table;
